import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import RichTextEditor from '../../components/RichTextEditor';
import { LayoutDashboard, Newspaper, BookOpen, Users, LogOut, ArrowLeft, Save, Video, Upload, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBookEditor = () => {
  const { id } = useParams();
  const isEditing = id && id !== 'new';
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '', author: '', summary: '', isbn: '', collection: 'Histoire',
    publication_date: '', price: '', pages: '', cover_image_url: '',
    author_bio: '', excerpt: '', purchase_url: '', status: 'draft'
  });
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const collections = ['Histoire', 'Politique', 'Économie', 'Faits de société', 'Géopolitique'];

  useEffect(() => { if (isEditing) fetchBook(); }, [id]);

  const fetchBook = async () => {
    try {
      const response = await fetch(`${API_URL}/api/books/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setFormData({
          title: data.title || '', author: data.author || '', summary: data.summary || '',
          isbn: data.isbn || '', collection: data.collection || 'Histoire',
          publication_date: data.publication_date || '', price: data.price || '',
          pages: data.pages || '', cover_image_url: data.cover_image_url || '',
          author_bio: data.author_bio || '', excerpt: data.excerpt || '',
          purchase_url: data.purchase_url || '', status: data.status || 'draft'
        });
        if (data.cover_image_url) setImagePreview(data.cover_image_url);
      } else {
        toast.error('Livre non trouvé');
        navigate('/admin/books');
      }
    } catch (error) { toast.error('Erreur'); } finally { setLoading(false); }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setFormData({ ...formData, cover_image_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const url = isEditing ? `${API_URL}/api/books/${id}` : `${API_URL}/api/books`;
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        toast.success(isEditing ? 'Livre mis à jour' : 'Livre créé');
        navigate('/admin/books');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const handleLogout = () => { logout(); navigate('/admin/login'); };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Tableau de bord', path: '/admin' },
    { icon: Newspaper, label: 'Actualités', path: '/admin/news' },
    { icon: BookOpen, label: 'Livres', path: '/admin/books', active: true },
    { icon: Video, label: 'Interviews', path: '/admin/videos' },
    { icon: Users, label: 'Utilisateurs', path: '/admin/users' }
  ];

  if (loading) return <div className="min-h-screen bg-stone-100 flex items-center justify-center"><p className="text-stone-600">Chargement...</p></div>;

  return (
    <div className="min-h-screen bg-stone-100 flex">
      <aside className="w-64 bg-stone-900 text-white flex flex-col">
        <div className="p-6 border-b border-stone-700">
          <h1 className="text-xl font-serif font-bold">Quid Novi</h1>
          <p className="text-stone-400 text-sm mt-1">Administration</p>
        </div>
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${item.active ? 'bg-hermes-600 text-white' : 'text-stone-300 hover:bg-stone-800'}`}>
                  <item.icon className="w-5 h-5" /><span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t border-stone-700">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-stone-400 hover:text-white hover:bg-stone-800 rounded transition-colors">
            <LogOut className="w-5 h-5" /><span>Déconnexion</span>
          </button>
        </div>
      </aside>

      <main className="flex-1 p-8 overflow-y-auto">
        <div className="mb-8">
          <Link to="/admin/books" className="inline-flex items-center gap-2 text-stone-600 hover:text-hermes-600 transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" />Retour au catalogue
          </Link>
          <h2 className="text-2xl font-serif font-bold text-stone-900">{isEditing ? 'Modifier le livre' : 'Nouveau livre'}</h2>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cover Image */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow-sm p-6">
              <h3 className="font-medium text-stone-900 mb-4">Couverture</h3>
              <div className="aspect-[2/3] bg-stone-100 rounded overflow-hidden mb-4 relative">
                {imagePreview ? (
                  <img src={imagePreview} alt="Couverture" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-stone-400">
                    <BookOpen className="w-16 h-16" />
                  </div>
                )}
              </div>
              <label className="flex items-center justify-center gap-2 px-4 py-2 border border-stone-300 text-stone-700 hover:bg-stone-50 cursor-pointer transition-colors">
                <Upload className="w-4 h-4" />
                <span>Télécharger une image</span>
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
              </label>
              <p className="text-xs text-stone-500 mt-2 text-center">JPG, PNG ou WebP. Max 5MB.</p>
            </div>
          </div>

          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white shadow-sm p-6 space-y-4">
              <h3 className="font-medium text-stone-900 mb-4">Informations principales</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-stone-700 mb-1">Titre *</label>
                  <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-3 py-2 border border-stone-300 focus:border-hermes-600 outline-none" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Auteur *</label>
                  <input type="text" value={formData.author} onChange={(e) => setFormData({...formData, author: e.target.value})} className="w-full px-3 py-2 border border-stone-300 focus:border-hermes-600 outline-none" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Collection</label>
                  <select value={formData.collection} onChange={(e) => setFormData({...formData, collection: e.target.value})} className="w-full px-3 py-2 border border-stone-300 focus:border-hermes-600 outline-none">
                    {collections.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">ISBN</label>
                  <input type="text" value={formData.isbn} onChange={(e) => setFormData({...formData, isbn: e.target.value})} className="w-full px-3 py-2 border border-stone-300 focus:border-hermes-600 outline-none" placeholder="978-2-xxx-xxxxx-x" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Date de publication</label>
                  <input type="date" value={formData.publication_date} onChange={(e) => setFormData({...formData, publication_date: e.target.value})} className="w-full px-3 py-2 border border-stone-300 focus:border-hermes-600 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Prix (€)</label>
                  <input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} className="w-full px-3 py-2 border border-stone-300 focus:border-hermes-600 outline-none" placeholder="19.90" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Nombre de pages</label>
                  <input type="number" value={formData.pages} onChange={(e) => setFormData({...formData, pages: e.target.value})} className="w-full px-3 py-2 border border-stone-300 focus:border-hermes-600 outline-none" placeholder="320" />
                </div>
              </div>
            </div>

            <div className="bg-white shadow-sm p-6">
              <h3 className="font-medium text-stone-900 mb-4">Résumé / 4ème de couverture *</h3>
              <RichTextEditor value={formData.summary} onChange={(v) => setFormData({...formData, summary: v})} placeholder="Présentez le livre..." />
            </div>

            <div className="bg-white shadow-sm p-6">
              <h3 className="font-medium text-stone-900 mb-4">Biographie de l'auteur</h3>
              <RichTextEditor value={formData.author_bio} onChange={(v) => setFormData({...formData, author_bio: v})} placeholder="Qui est l'auteur ?" />
            </div>

            <div className="bg-white shadow-sm p-6">
              <h3 className="font-medium text-stone-900 mb-4">Extrait du livre</h3>
              <RichTextEditor value={formData.excerpt} onChange={(v) => setFormData({...formData, excerpt: v})} placeholder="Un extrait pour donner envie..." />
            </div>

            <div className="bg-white shadow-sm p-6">
              <h3 className="font-medium text-stone-900 mb-4">Lien d'achat</h3>
              <div className="flex items-center gap-2">
                <ExternalLink className="w-5 h-5 text-stone-400" />
                <input type="url" value={formData.purchase_url} onChange={(e) => setFormData({...formData, purchase_url: e.target.value})} className="flex-1 px-3 py-2 border border-stone-300 focus:border-hermes-600 outline-none" placeholder="https://va-editions.fr/livre/..." />
              </div>
              <p className="text-xs text-stone-500 mt-2">Lien vers la page d'achat sur va-editions.fr</p>
            </div>

            <div className="bg-white shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <input type="checkbox" id="published" checked={formData.status === 'published'} onChange={(e) => setFormData({...formData, status: e.target.checked ? 'published' : 'draft'})} className="w-5 h-5" />
                  <label htmlFor="published" className="text-sm font-medium text-stone-700">Publier immédiatement</label>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button type="submit" disabled={saving || !formData.title || !formData.author || !formData.summary} className="flex items-center gap-2 px-6 py-3 bg-hermes-600 text-white font-medium hover:bg-hermes-700 transition-colors disabled:opacity-50">
                <Save className="w-5 h-5" />{saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <Link to="/admin/books" className="px-6 py-3 border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors">Annuler</Link>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminBookEditor;
